import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Badge,
  Tooltip,
  Switch,
  Divider,
  Alert,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ShoppingOutlined,
  BarcodeOutlined,
  TagsOutlined,
  DollarOutlined,
  InboxOutlined,
  UploadOutlined,
  MinusCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import ModernFormModal from '../components/ModernFormModal';
import { masterDataService } from '../services/masterDataService';
import { Product, Category, Location } from '../services/masterDataService';
import { inventoryService } from '../services/inventoryService';
import { useResponsive } from '../hooks/useResponsive';
import { PERMISSIONS, RBACService } from '../utils/rbac';
import { exportRowsToExcel } from '../utils/exportToExcel';
import { ProductPackagingFields } from '../components/products/ProductPackagingFields';
import './Products.css';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Normalize parent_id from mixed/legacy payloads.
 * Treat null/undefined/empty/0/non-numeric as "no parent".
 */
function normalizeParentId(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim().toLowerCase();
  if (!text || text === 'null' || text === 'none' || text === 'undefined') return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function getCategoryParentId(category: Category): number | null {
  return normalizeParentId((category as any).parent_id);
}

/** Path from root to category leaf (for Ant Design Cascader). */
function categoryPathToLeaf(categories: Category[], leafId: number): number[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const path: number[] = [];
  let id: number | undefined = leafId;
  while (id !== undefined) {
    const c = byId.get(id);
    if (!c) break;
    path.unshift(c.id);
    id = getCategoryParentId(c) ?? undefined;
  }
  return path;
}

type CascaderOption = { value: number; label: string; children?: CascaderOption[] };

/** Build parent → child tree; products use the selected leaf category id. */
function buildCategoryCascaderOptions(categories: Category[]): CascaderOption[] {
  const byParent = new Map<number | null, Category[]>();
  for (const c of categories) {
    const p = getCategoryParentId(c);
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p)!.push(c);
  }
  const sort = (a: Category, b: Category) => a.name.localeCompare(b.name);
  function buildNode(cat: Category): CascaderOption {
    const kids = (byParent.get(cat.id) || []).slice().sort(sort);
    if (kids.length === 0) {
      return { value: cat.id, label: cat.name };
    }
    return { value: cat.id, label: cat.name, children: kids.map(buildNode) };
  }
  return (byParent.get(null) || []).slice().sort(sort).map(buildNode);
}

function gstCategoryLabel(raw?: string | null): string {
  const value = String(raw || 'taxable').trim().toLowerCase();
  if (value === 'exempt') return 'Exempt';
  if (value === 'zero_rated' || value === 'zero' || value === 'zero-rated') return 'Zero-rated';
  return 'Taxable 5%';
}

function gstCategoryTagColor(raw?: string | null): string {
  const value = String(raw || 'taxable').trim().toLowerCase();
  if (value === 'exempt') return 'default';
  if (value === 'zero_rated' || value === 'zero' || value === 'zero-rated') return 'purple';
  return 'green';
}

function formatCategoryBreadcrumb(categories: Category[], leafId: number | undefined | null): string {
  if (leafId == null) return '—';
  return categoryPathToLeaf(categories, leafId)
    .map((id) => categories.find((c) => c.id === id)?.name || '?')
    .join(' › ');
}

function collectDescendantCategoryIds(categories: Category[], rootId: number): Set<number> {
  const ids = new Set<number>([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const c of categories) {
      const parentId = getCategoryParentId(c);
      if (parentId != null && ids.has(parentId) && !ids.has(c.id)) {
        ids.add(c.id);
        changed = true;
      }
    }
  }
  return ids;
}

const UOM_OTHER_VALUE = '__other__';
const PRODUCT_FETCH_CHUNK = 300;
const UOM_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'pieces', label: 'Pieces' },
  { value: 'kg', label: 'Kilograms' },
  { value: 'liters', label: 'Liters' },
  { value: 'meters', label: 'Meters' },
  { value: 'boxes', label: 'Boxes' },
  { value: 'bags', label: 'Bags' },
];

const Products: React.FC = () => {
  const { isMobile } = useResponsive();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [viewProductModalVisible, setViewProductModalVisible] = useState(false);
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showNewSubcategoryInput, setShowNewSubcategoryInput] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingSubcategory, setAddingSubcategory] = useState(false);
  const [categoryPermissionHint, setCategoryPermissionHint] = useState('');
  const [subcategoryPermissionHint, setSubcategoryPermissionHint] = useState('');
  const [inventoryStats, setInventoryStats] = useState<{
    low_stock_products: number;
    total_stock_units: number;
  } | null>(null);
  /** Live inventory quantities when editing (keyed by product id in API). */
  const [editStockLoading, setEditStockLoading] = useState(false);
  const [editStockError, setEditStockError] = useState(false);
  const [editStockSnapshot, setEditStockSnapshot] = useState<{
    totalOnHand: number;
    lines: Array<{
      locationId: number;
      name: string;
      onHand: number;
      manufactureDate?: string | null;
      expiryDate?: string | null;
      expiryLabel?: string | null;
    }>;
  } | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  /** When true, product update must send hands-on total from inventory (see handleSubmit). */
  const canUseHandsOnForMinStockRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchText(searchText.trim()), 250);
    return () => clearTimeout(t);
  }, [searchText]);

  const loadInventoryStats = useCallback(async () => {
    try {
      const data = await inventoryService.getDashboard();
      setInventoryStats({
        low_stock_products: data.stock_summary.low_stock_products,
        total_stock_units: data.stock_summary.total_stock_units,
      });
    } catch {
      setInventoryStats(null);
    }
  }, []);

  // Fetch inventory stats for cards
  useEffect(() => {
    loadInventoryStats();
  }, [loadInventoryStats]);

  const clearEditStockSnapshot = useCallback(() => {
    setEditStockLoading(false);
    setEditStockError(false);
    setEditStockSnapshot(null);
    canUseHandsOnForMinStockRef.current = false;
  }, []);

  const fetchInventoryStockForProduct = useCallback(async (productId: number) => {
    setEditStockLoading(true);
    setEditStockError(false);
    setEditStockSnapshot(null);
    canUseHandsOnForMinStockRef.current = false;
    try {
      const CHUNK = 300;
      let skip = 0;
      const lines: Array<{
        locationId: number;
        name: string;
        onHand: number;
        manufactureDate?: string | null;
        expiryDate?: string | null;
        expiryLabel?: string | null;
      }> = [];
      for (;;) {
        const res = await inventoryService.getDetailedStock({
          product_id: productId,
          skip,
          limit: CHUNK,
          include_recent_receipts: false,
        });
        const items = res.items || [];
        for (const it of items) {
          lines.push({
            locationId: it.location.id,
            name: it.location.name,
            onHand: it.quantities.on_hand,
            manufactureDate: it.manufacture_date,
            expiryDate: it.expiry_date,
            expiryLabel: it.expiry_label,
          });
        }
        if (items.length < CHUNK) break;
        skip += items.length;
      }
      const totalOnHand = lines.reduce((s, x) => s + x.onHand, 0);
      setEditStockSnapshot({ totalOnHand, lines });
      canUseHandsOnForMinStockRef.current = true;
    } catch {
      setEditStockError(true);
      setEditStockSnapshot(null);
      canUseHandsOnForMinStockRef.current = false;
    } finally {
      setEditStockLoading(false);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const data = await masterDataService.getLocations({ active_only: true, limit: 200 });
      setLocations(data);
    } catch {
      setLocations([]);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const editHydratedFor = useRef<number | null>(null);
  /** Put the saved sub-category and dates back once the category list is ready. */
  useEffect(() => {
    if (!modalVisible) {
      editHydratedFor.current = null;
      return;
    }
    if (!editingProduct || categories.length === 0) return;
    if (editHydratedFor.current === editingProduct.id) return;
    const saved = categories.find((c) => c.id === editingProduct.category_id);
    const savedParent = saved ? getCategoryParentId(saved) : null;
    const parentId = savedParent ?? saved?.id ?? editingProduct.category_id;
    const subcategoryId = saved && savedParent != null ? saved.id : undefined;
    form.setFieldsValue({
      category_id: parentId,
      subcategory_id: subcategoryId,
      manufacture_date: editingProduct.manufacture_date ? moment(editingProduct.manufacture_date) : undefined,
      expiry_date: editingProduct.expiry_date ? moment(editingProduct.expiry_date) : undefined,
      expiry_alert_days: editingProduct.expiry_alert_days ?? 30,
    });
    editHydratedFor.current = editingProduct.id;
  }, [modalVisible, editingProduct, categories, form]);

  /** Edit product: keep catalog minimum_stock_level aligned with hands-on total from inventory. */
  useEffect(() => {
    if (!modalVisible || !editingProduct || editStockLoading || editStockError || !editStockSnapshot) return;
    form.setFieldsValue({ minimum_stock_level: editStockSnapshot.totalOnHand });
  }, [modalVisible, editingProduct, editStockLoading, editStockError, editStockSnapshot, form]);

  // Fetch data from backend
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      let skip = 0;
      const all: Product[] = [];
      for (;;) {
        const chunk = await masterDataService.getProducts({
          skip,
          limit: PRODUCT_FETCH_CHUNK,
          active_only: true,
          search: debouncedSearchText || undefined,
        });
        all.push(...chunk);
        if (chunk.length < PRODUCT_FETCH_CHUNK) break;
        skip += chunk.length;
      }
      setProducts(all);
    } catch (error) {
      message.error('Failed to fetch products');
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchText]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await masterDataService.getCategories({
        active_only: true,
        limit: 500,
      });
      setCategories(data);
    } catch (error) {
      message.error('Failed to fetch categories');
      console.error('Error fetching categories:', error);
    }
  }, []);

  const topLevelCategories = useMemo(
    () => {
      const ids = new Set(categories.map((c) => Number(c.id)));
      // Include true roots and orphaned rows (parent not found in current dataset)
      // so users can still select/create subcategories on partially-migrated data.
      return categories
        .filter((c) => {
          const parentId = getCategoryParentId(c);
          return parentId == null || !ids.has(parentId);
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    [categories],
  );

  const navigate = useNavigate();
  const canCreateCategory = RBACService.hasPermission(PERMISSIONS.CATEGORY_CREATE);
  const canUpdateProduct = RBACService.hasPermission(PERMISSIONS.PRODUCT_UPDATE);
  const canDeleteProduct = RBACService.hasPermission(PERMISSIONS.PRODUCT_DELETE);
  const canImportProducts = RBACService.hasPermission(PERMISSIONS.PRODUCT_CREATE);
  const canExportProducts = RBACService.hasPermission(PERMISSIONS.PRODUCT_READ);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  const searchLower = debouncedSearchText.trim().toLowerCase();

  const allowedCategoryIds = useMemo(
    () => (selectedCategory ? collectDescendantCategoryIds(categories, selectedCategory) : null),
    [categories, selectedCategory],
  );

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch =
        searchLower === '' ||
        product.name.toLowerCase().includes(searchLower) ||
        product.code.toLowerCase().includes(searchLower) ||
        (product.barcode && String(product.barcode).toLowerCase().includes(searchLower));
      const matchesCategory =
        !selectedCategory ||
        (product.category_id != null && allowedCategoryIds?.has(product.category_id));
      // Low stock: items with less than 3 pieces
      const matchesLowStock = !lowStockOnly || (product.stock_level ?? 0) < 3;
      return matchesSearch && matchesCategory && matchesLowStock && product.is_active;
    });
  }, [products, searchLower, selectedCategory, allowedCategoryIds, lowStockOnly]);

  const loadProducts = () => {
    fetchProducts();
  };

  const handleCreate = () => {
    clearEditStockSnapshot();
    setEditingProduct(null);
    setShowNewCategoryInput(false);
    setShowNewSubcategoryInput(false);
    setCategoryPermissionHint('');
    setSubcategoryPermissionHint('');
    form.resetFields();
    form.setFieldsValue({
      unit_of_measure: 'pieces',
      is_active: true,
      category_id: undefined,
      subcategory_id: undefined,
      subcategory_name: undefined,
      minimum_stock_level: 0,
      inventory_location_id: undefined,
      initial_location_stocks: [{ location_id: undefined, quantity: 0 }],
      gst_category: 'taxable',
      manufacture_date: undefined,
      expiry_date: undefined,
      expiry_alert_days: 30,
    });
    setModalVisible(true);
  };

  const handleViewProduct = (product: Product) => {
    setViewingProduct(product);
    setViewProductModalVisible(true);
  };

  const handleEdit = (product: Product) => {
    clearEditStockSnapshot();
    setEditingProduct(product);
    setShowNewCategoryInput(false);
    setShowNewSubcategoryInput(false);
    setCategoryPermissionHint('');
    setSubcategoryPermissionHint('');
    const uom = String((product as any)?.unit_of_measure || '').trim();
    const knownUom = UOM_OPTIONS.some((o) => o.value === uom);
    const leafCategory = product.category_id ? categories.find((c) => c.id === product.category_id) : undefined;
    const leafParentId = leafCategory ? getCategoryParentId(leafCategory) : null;
    const parentCategoryId = leafParentId ?? leafCategory?.id;
    const isLeafTopLevel = leafParentId == null;
    form.setFieldsValue({
      ...product,
      minimum_stock_level: product.minimum_stock_level ?? 0,
      unit_price: product.selling_price,
      wholesale_price: product.wholesale_price ?? undefined,
      pack_size: product.pack_size ?? undefined,
      carton_size: product.carton_size ?? undefined,
      pack_selling_price: product.pack_selling_price ?? undefined,
      pack_wholesale_price: product.pack_wholesale_price ?? undefined,
      carton_selling_price: product.carton_selling_price ?? undefined,
      carton_wholesale_price: product.carton_wholesale_price ?? undefined,
      bag_size: product.bag_size ?? undefined,
      bag_selling_price: product.bag_selling_price ?? undefined,
      bag_wholesale_price: product.bag_wholesale_price ?? undefined,
      unit_of_measure: knownUom ? uom : UOM_OTHER_VALUE,
      unit_of_measure_other: knownUom ? undefined : uom,
      category_id: parentCategoryId,
      subcategory_id: isLeafTopLevel ? undefined : leafCategory?.id,
      subcategory_name: undefined,
      gst_category: product.gst_category || 'taxable',
      manufacture_date: product.manufacture_date ? moment(product.manufacture_date) : undefined,
      expiry_date: product.expiry_date ? moment(product.expiry_date) : undefined,
      expiry_alert_days: product.expiry_alert_days ?? 30,
    });
    setModalVisible(true);
    void fetchInventoryStockForProduct(product.id);
  };

  const handleAddCategoryInline = async () => {
    const name = String(form.getFieldValue('category_name') || '').trim();
    if (!name) {
      message.error('Please enter new category name');
      return;
    }
    try {
      setAddingCategory(true);
      setCategoryPermissionHint('');
      const created = await masterDataService.createCategory({ name, parent_id: null, is_active: true });
      await fetchCategories();
      form.setFieldsValue({
        category_id: created.id,
        category_name: undefined,
        subcategory_id: undefined,
        subcategory_name: undefined,
      });
      setShowNewCategoryInput(false);
      setShowNewSubcategoryInput(false);
      setCategoryPermissionHint('');
      message.success('Category added');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('missing permission')) {
        setCategoryPermissionHint(detail);
      } else {
        message.error(typeof detail === 'string' ? detail : 'Failed to add category');
      }
    } finally {
      setAddingCategory(false);
    }
  };

  const handleAddSubcategoryInline = async () => {
    const parentId = form.getFieldValue('category_id') as number | undefined;
    const name = String(form.getFieldValue('subcategory_name') || '').trim();
    if (!parentId) {
      message.error('Please select a category first');
      return;
    }
    if (!name) {
      message.error('Please enter new sub-category name');
      return;
    }
    try {
      setAddingSubcategory(true);
      setSubcategoryPermissionHint('');
      const created = await masterDataService.createCategory({ name, parent_id: parentId, is_active: true });
      await fetchCategories();
      form.setFieldsValue({ subcategory_id: created.id, subcategory_name: undefined });
      setShowNewSubcategoryInput(false);
      setSubcategoryPermissionHint('');
      message.success('Sub-category added');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('missing permission')) {
        setSubcategoryPermissionHint(detail);
      } else {
        message.error(typeof detail === 'string' ? detail : 'Failed to add sub-category');
      }
    } finally {
      setAddingSubcategory(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const resolvedUom =
        values.unit_of_measure === UOM_OTHER_VALUE
          ? String(values.unit_of_measure_other || '').trim()
          : String(values.unit_of_measure || '').trim();

      const selectedCategoryId = values.category_id as number | undefined;
      const requestedTopCategoryName = String(values.category_name || '').trim();
      let resolvedTopCategoryId = selectedCategoryId;

      if (!resolvedTopCategoryId && requestedTopCategoryName) {
        if (!canCreateCategory) {
          message.error('Missing permission: category_create. Ask admin to grant it.');
          setLoading(false);
          return;
        }
        const createdTopCategory = await masterDataService.createCategory({
          name: requestedTopCategoryName,
          parent_id: null,
          is_active: true,
        });
        resolvedTopCategoryId = createdTopCategory.id;
        await fetchCategories();
      }

      if (!resolvedTopCategoryId) {
        message.error('Please select a category.');
        setLoading(false);
        return;
      }

      let leafCategoryId: number | undefined = values.subcategory_id as number | undefined;
      const requestedSubcategoryName = String(values.subcategory_name || '').trim();

      if (!leafCategoryId && requestedSubcategoryName) {
        if (!canCreateCategory) {
          message.error('Missing permission: category_create. Ask admin to grant it, or choose an existing subcategory.');
          setLoading(false);
          return;
        }
        const createdSubcategory = await masterDataService.createCategory({
          name: requestedSubcategoryName,
          parent_id: resolvedTopCategoryId,
          is_active: true,
        });
        leafCategoryId = createdSubcategory.id;
        await fetchCategories();
      }

      if (!leafCategoryId && editingProduct?.category_id) {
        const saved = categories.find((c) => c.id === editingProduct.category_id);
        const savedParent = saved ? getCategoryParentId(saved) : null;
        if (saved && savedParent != null && savedParent === resolvedTopCategoryId) {
          leafCategoryId = saved.id;
        }
      }

      if (!leafCategoryId) {
        leafCategoryId = resolvedTopCategoryId;
      }

      const rawBarcode = values.barcode != null ? String(values.barcode).trim() : '';
      const handsOnMin =
        editingProduct &&
        editStockSnapshot &&
        !editStockError &&
        canUseHandsOnForMinStockRef.current
          ? editStockSnapshot.totalOnHand
          : null;
      const minimumStockLevel =
        handsOnMin !== null ? handsOnMin : Number(values.minimum_stock_level ?? 0);

      const productPayload: Record<string, unknown> = {
        name: values.name,
        code: '',
        category_id: leafCategoryId,
        description: values.description || '',
        unit_of_measure: resolvedUom || 'pieces',
        cost_price: Number(values.cost_price ?? 0),
        selling_price: Number(values.unit_price ?? 0),
        wholesale_price:
          values.wholesale_price != null && values.wholesale_price !== ''
            ? Number(values.wholesale_price)
            : null,
        pack_size:
          values.pack_size != null && Number(values.pack_size) > 1 ? Number(values.pack_size) : null,
        carton_size:
          values.carton_size != null && Number(values.carton_size) > 1
            ? Number(values.carton_size)
            : null,
        pack_selling_price:
          values.pack_selling_price != null && values.pack_selling_price !== ''
            ? Number(values.pack_selling_price)
            : null,
        pack_wholesale_price:
          values.pack_wholesale_price != null && values.pack_wholesale_price !== ''
            ? Number(values.pack_wholesale_price)
            : null,
        carton_selling_price:
          values.carton_selling_price != null && values.carton_selling_price !== ''
            ? Number(values.carton_selling_price)
            : null,
        carton_wholesale_price:
          values.carton_wholesale_price != null && values.carton_wholesale_price !== ''
            ? Number(values.carton_wholesale_price)
            : null,
        bag_size:
          values.bag_size != null && Number(values.bag_size) > 1 ? Number(values.bag_size) : null,
        bag_selling_price:
          values.bag_selling_price != null && values.bag_selling_price !== ''
            ? Number(values.bag_selling_price)
            : null,
        bag_wholesale_price:
          values.bag_wholesale_price != null && values.bag_wholesale_price !== ''
            ? Number(values.bag_wholesale_price)
            : null,
        gst_category: values.gst_category || 'taxable',
        minimum_stock_level: minimumStockLevel,
        is_active: values.is_active ?? true,
        ...(rawBarcode ? { barcode: rawBarcode } : {}),
      };

      if (!editingProduct) {
        productPayload.sync_inventory = true;
        const listRaw =
          (values.initial_location_stocks as Array<{ location_id?: number; quantity?: number }> | undefined) || [];
        for (const r of listRaw) {
          const q = Math.max(0, Number(r?.quantity) || 0);
          const lid = r?.location_id;
          if (q > 0 && (lid == null || Number.isNaN(Number(lid)))) {
            message.error('Select a location for each row where quantity is greater than 0.');
            setLoading(false);
            return;
          }
        }
        const withStock = listRaw
          .map((r) => ({
            location_id: r?.location_id != null ? Number(r.location_id) : null,
            quantity: Math.max(0, Number(r?.quantity) || 0),
          }))
          .filter((r) => r.location_id != null && r.quantity > 0) as Array<{ location_id: number; quantity: number }>;

        if (withStock.length > 0) {
          productPayload.initial_location_stocks = withStock;
        } else {
          productPayload.opening_quantity = 0;
          const invLoc = values.inventory_location_id as number | undefined;
          if (invLoc != null) {
            productPayload.inventory_location_id = invLoc;
          }
        }
      }

      if (editingProduct) {
        await masterDataService.updateProduct(editingProduct.id, {
          name: productPayload.name,
          category_id: leafCategoryId,
          description: productPayload.description,
          unit_of_measure: productPayload.unit_of_measure,
          cost_price: productPayload.cost_price,
          selling_price: productPayload.selling_price,
          wholesale_price: productPayload.wholesale_price,
          pack_size: productPayload.pack_size,
          carton_size: productPayload.carton_size,
          pack_selling_price: productPayload.pack_selling_price,
          pack_wholesale_price: productPayload.pack_wholesale_price,
          carton_selling_price: productPayload.carton_selling_price,
          carton_wholesale_price: productPayload.carton_wholesale_price,
          bag_size: productPayload.bag_size,
          bag_selling_price: productPayload.bag_selling_price,
          bag_wholesale_price: productPayload.bag_wholesale_price,
          gst_category: productPayload.gst_category,
          minimum_stock_level: productPayload.minimum_stock_level,
          is_active: productPayload.is_active,
          ...(rawBarcode ? { barcode: rawBarcode } : {}),
        });
        message.success('Product updated successfully!');
      } else {
        await masterDataService.createProduct(productPayload);
        // After create, align table filters so the newly created item is visible immediately.
        setSelectedCategory(resolvedTopCategoryId ?? null);
        setSearchText('');
        setLowStockOnly(false);
        message.success('Product created successfully!');
        loadInventoryStats();
      }

      setModalVisible(false);
      form.resetFields();
      setEditingProduct(null);
      clearEditStockSnapshot();
      loadProducts(); // Refresh the product list
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Please fill in all required fields');
      } else if (error.response) {
        const detail = error.response?.data?.detail;
        message.error(typeof detail === 'string' ? detail : (editingProduct ? 'Failed to update product' : 'Failed to create product'));
      }
      console.error('Error submitting product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId: number) => {
    try {
      await masterDataService.deleteProduct(productId);
      message.success('Product deactivated successfully!');
      loadProducts();
    } catch (error) {
      message.error('Failed to deactivate product');
      console.error('Error deactivating product:', error);
    }
  };

  const exportProductsToExcel = () => {
    try {
      if (filteredProducts.length === 0) {
        message.info('No products to export');
        return;
      }
      exportRowsToExcel(
        filteredProducts,
        [
          { header: 'Product Name', value: (p) => p.name },
          { header: 'Material ID', value: (p) => p.code },
          { header: 'Barcode', value: (p) => p.barcode || '' },
          {
            header: 'Category',
            value: (p) => formatCategoryBreadcrumb(categories, p.category_id) || p.category?.name || '',
          },
          { header: 'Unit of Measure', value: (p) => p.unit_of_measure || '' },
          { header: 'Cost Price', value: (p) => p.cost_price ?? '' },
          { header: 'Selling Price', value: (p) => p.selling_price ?? '' },
          { header: 'Stock Level', value: (p) => p.stock_level ?? '' },
          { header: 'Reorder Level', value: (p) => p.reorder_level ?? '' },
          { header: 'Minimum Stock Level', value: (p) => p.minimum_stock_level ?? '' },
          { header: 'Status', value: (p) => (p.is_active ? 'Active' : 'Inactive') },
          {
            header: 'Created',
            value: (p) => (p.created_at ? moment(p.created_at).format('YYYY-MM-DD') : ''),
          },
        ],
        `products_${moment().format('YYYYMMDD_HHmmss')}.xlsx`,
        'Products',
      );
      message.success(`Exported ${filteredProducts.length} product(s) to Excel`);
    } catch (error) {
      console.error('Product export failed:', error);
      message.error('Failed to export products');
    }
  };

  const columns: ColumnsType<Product> = [
    {
      title: 'Product',
      key: 'product',
      width: 200,
      render: (_, record) => (
        <Space>
          <div>
            <div style={{ fontWeight: 500 }}>{record.name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <BarcodeOutlined /> Material ID: {record.code}
            </div>
            {record.barcode != null && String(record.barcode).trim() !== '' && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                Barcode: {record.barcode}
              </div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (_, record) => (
        <Tag color="blue" icon={<TagsOutlined />}>
          {formatCategoryBreadcrumb(categories, record.category_id) || record.category?.name || 'No Category'}
        </Tag>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'selling_price',
      key: 'selling_price',
      width: 100,
      render: (price: number) => (
        <Space>
          <DollarOutlined />
          {price?.toLocaleString() || '0'}
        </Space>
      ),
    },
    {
      title: 'GST',
      dataIndex: 'gst_category',
      key: 'gst_category',
      width: 120,
      render: (gst_category: string) => (
        <Tag color={gstCategoryTagColor(gst_category)}>{gstCategoryLabel(gst_category)}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (is_active: boolean) => (
        <Badge
          status={is_active ? 'success' : 'error'}
          text={is_active ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 168,
      render: (_, record) => (
        <Space wrap={false} size={8} style={{ display: 'inline-flex' }}>
          <Tooltip title="View" mouseEnterDelay={0.2}>
            <Button
              aria-label="View product"
              className="icon-btn"
              shape="round"
              icon={<EyeOutlined />}
              onClick={() => handleViewProduct(record)}
            />
          </Tooltip>
          {canUpdateProduct && (
            <Tooltip title="Edit" mouseEnterDelay={0.2}>
              <Button
                aria-label="Edit"
                className="icon-btn icon-btn--primary"
                shape="round"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {canDeleteProduct && record.is_active && (
            <Popconfirm
              title="Deactivate this product?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Deactivate" mouseEnterDelay={0.2}>
                <Button
                  aria-label="Deactivate"
                  className="icon-btn icon-btn--danger"
                  shape="round"
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const totalProducts = products.filter((p) => p.is_active).length;
  // Low stock: from inventory dashboard when available, else from product stock_level
  const lowStockProducts = inventoryStats?.low_stock_products ?? products.filter((p) => {
    if (!p.is_active) return false;
    const stockLevel = p.stock_level ?? 0;
    return stockLevel < 3;
  }).length;
  return (
    <div className="products-page" style={{ minHeight: '100%', overflowY: 'visible', position: 'relative' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" className="products-header" gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <div className="products-header__title-wrap">
            <div className="products-header__icon"><ShoppingOutlined /></div>
            <div>
              <Title level={2} className="products-header__title">Products</Title>
              <p className="products-header__subtitle">Manage your product inventory and pricing</p>
            </div>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <Space 
            wrap 
            style={{ width: '100%', justifyContent: isMobile ? 'stretch' : 'flex-end' }}
            direction={isMobile ? 'vertical' : 'horizontal'}
            size="small"
          >
            <Input.Search
              className="app-search"
              placeholder="Search products or material ID..."
              allowClear
              prefix={null}
              value={searchText}
              onSearch={(value) => setSearchText(value)}
              onChange={(e) => setSearchText(e.target.value)}
              enterButton
              style={{ width: isMobile ? '100%' : 200 }}
              size="large"
            />
            <Select
              placeholder="All Categories"
              allowClear
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ width: isMobile ? '100%' : 150 }}
              size="large"
              suffixIcon={null}
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {formatCategoryBreadcrumb(categories, cat.id)}
                </Option>
              ))}
            </Select>
            {canExportProducts && (
              <Button
                icon={<DownloadOutlined />}
                onClick={exportProductsToExcel}
                size="large"
                block={isMobile}
              >
                Export Excel
              </Button>
            )}
            {canImportProducts && (
              <Button
                icon={<UploadOutlined />}
                onClick={() => navigate('/products/import-excel')}
                size="large"
                block={isMobile}
              >
                Import Excel
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="large"
              block={isMobile}
            >
              Add Product
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards - same style as Inventory */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card 
            className="page-stat-card page-stat-card--blue"
            hoverable
            onClick={() => {
              setLowStockOnly(false);
              setSelectedCategory(null);
              setSearchText('');
            }}
            style={{ cursor: 'pointer', border: !lowStockOnly ? '2px solid #1890ff' : undefined }}
          >
            <Statistic
              title="Total Products"
              value={totalProducts ?? 0}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#1890ff', fontWeight: 400, fontSize: '12px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card 
            className="page-stat-card page-stat-card--amber"
            hoverable
            onClick={() => {
              setLowStockOnly(true);
              setSelectedCategory(null);
              setSearchText('');
            }}
            style={{ cursor: 'pointer', border: lowStockOnly ? '2px solid #faad14' : undefined }}
          >
            <Statistic
              title="Low Stock Items"
              value={lowStockProducts ?? 0}
              prefix={<BarcodeOutlined />}
              valueStyle={{ color: '#faad14', fontWeight: 'bold', fontSize: '22px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card className="page-stat-card page-stat-card--green">
            <Statistic
              title="Total Stock Units"
              value={inventoryStats?.total_stock_units ?? 0}
              prefix={<InboxOutlined />}
              valueStyle={{ color: '#52c41a', fontWeight: 400, fontSize: '12px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Products Table */}
      <Card 
        className="page-table-card page-table-card--indigo"
        bodyStyle={{ 
          overflow: 'visible',
          padding: 0,
          position: 'relative',
          minHeight: isMobile ? '300px' : '400px',
          display: 'flex',
          flexDirection: 'column',
          border: 'none'
        }}
        style={{ 
          position: 'relative',
          zIndex: 1,
          minHeight: isMobile ? '300px' : '400px',
          display: 'flex',
          flexDirection: 'column',
          border: 'none',
          boxShadow: 'none'
        }}
      >
        {filteredProducts.length === 0 && !loading ? (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <div className="empty-state-title">No products found</div>
            <div className="empty-state-description">
              {searchText || selectedCategory 
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by adding your first product'}
            </div>
            {!searchText && !selectedCategory && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleCreate}
                style={{ marginTop: 16 }}
                size="large"
              >
                Add Your First Product
              </Button>
            )}
          </div>
        ) : (
          <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Table
          columns={columns}
          dataSource={filteredProducts}
          rowKey="id"
          loading={loading}
          pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                showSizeChanger: !isMobile,
            showTotal: (total) => `Total ${total} products`,
                responsive: true,
                pageSizeOptions: ['5', '10', '20', '50', '100'],
                position: ['bottomCenter']
          }}
              scroll={{ 
                x: isMobile ? 800 : 1200
              }}
              size={isMobile ? 'small' : 'middle'}
              bordered={false}
            />
          </div>
        )}
      </Card>

      {/* Add/Edit Product Modal - full-area form like reference (fills content area, margin, rounded) */}
      <Modal
        title={
          <Space>
            <ShoppingOutlined />
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </Space>
        }
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          setEditingProduct(null);
          form.resetFields();
          clearEditStockSnapshot();
        }}
        getContainer={() => document.querySelector('.main-content-full') || document.body}
        wrapClassName="form-panel-full-wrap product-modal-form-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        okText={editingProduct ? 'Update product' : 'Save product'}
        okButtonProps={{
          disabled: !!editingProduct && editStockLoading,
          title: editingProduct && editStockLoading ? 'Loading inventory totals…' : undefined,
        }}
      >
        {/* Inner scroll area — form-panel-full-wrap sets modal body overflow:hidden; without this, fields below the fold are clipped */}
        <div className="mf-modal-body product-modal-form-inner">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            is_active: true,
            minimum_stock_level: 0,
          }}
          autoComplete="off"
        >
          {editingProduct && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Catalog vs inventory"
              description="Name, prices, and units are edited here. Physical quantities are changed in Inventory (GRPO, adjustments, sales). On Update, minimum stock level is saved as your current total on hand from inventory when that total has loaded successfully."
            />
          )}
          <div className="product-form-section-title">Product information</div>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Product Name"
                rules={[{ required: true, message: 'Please enter product name' }]}
              >
                <Input placeholder="e.g., Samsung Galaxy Phone" autoComplete="off" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="barcode"
                label="Barcode"
                tooltip="Optional. Leave blank to auto-generate. For POS, use the same digits as on the product label."
                extra="USB scanner: click in this box, then scan — the scanner types the code like a keyboard (no separate Scan button needed)."
                rules={[{ max: 100, message: 'Barcode must be at most 100 characters' }]}
              >
                <Input placeholder="Type or scan barcode (focus here first)" autoComplete="off" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align="top">
            <Col xs={24} md={12}>
              <Form.Item
                name="category_id"
                label={(
                  <Space size={6}>
                    <span>Category</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setShowNewCategoryInput(true);
                        form.setFieldsValue({ category_id: undefined, subcategory_id: undefined, subcategory_name: undefined });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShowNewCategoryInput(true);
                          form.setFieldsValue({ category_id: undefined, subcategory_id: undefined, subcategory_name: undefined });
                        }
                      }}
                      style={{ color: '#1677ff', fontWeight: 500, cursor: 'pointer', textDecoration: 'none' }}
                    >
                      + Add category
                    </span>
                  </Space>
                )}
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select
                  placeholder="Select category"
                  showSearch
                  optionFilterProp="children"
                  onChange={() => {
                    form.setFieldsValue({ subcategory_id: undefined, subcategory_name: undefined });
                    setShowNewCategoryInput(false);
                    setShowNewSubcategoryInput(false);
                  }}
                >
                  {topLevelCategories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {showNewCategoryInput && (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Form.Item
                      name="category_name"
                      style={{ flex: 1, marginBottom: 0, minWidth: 120 }}
                      tooltip={canCreateCategory ? 'Creates a top-level category.' : 'You do not have category_create permission.'}
                    >
                      <Input
                        placeholder="Enter new category name"
                        onChange={() => {
                          form.setFieldsValue({ category_id: undefined, subcategory_id: undefined, subcategory_name: undefined });
                        }}
                      />
                    </Form.Item>
                    <Button type="primary" onClick={handleAddCategoryInline} loading={addingCategory}>
                      Add
                    </Button>
                    <Button
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        form.setFieldsValue({ category_name: undefined });
                      setCategoryPermissionHint('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  {categoryPermissionHint && (
                    <div style={{ color: '#d4380d', fontSize: 12, marginTop: -6, marginBottom: 10 }}>
                      {categoryPermissionHint}
                    </div>
                  )}
                  {!canCreateCategory && (
                    <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: -6, marginBottom: 10 }}>
                      You can view this field, but adding category requires `category_create` permission.
                    </div>
                  )}
                </>
              )}
            </Col>

            <Col xs={24} md={12}>
              <Form.Item shouldUpdate={(prev, cur) => prev.category_id !== cur.category_id || prev.subcategory_id !== cur.subcategory_id} noStyle>
                {({ getFieldValue }) => {
                  const parentId = getFieldValue('category_id') as number | undefined;
                  const chosenSubcategory = getFieldValue('subcategory_id') as number | undefined;
                  if (!parentId) {
                    return (
                      <Form.Item label="Sub-category">
                        <Text type="secondary" style={{ lineHeight: '32px' }}>Select a category first</Text>
                      </Form.Item>
                    );
                  }
                  const subcategories = categories
                    .filter((c) => getCategoryParentId(c) === Number(parentId))
                    .sort((a, b) => a.name.localeCompare(b.name));
                  return (
                    <div className="product-form-subcategory-well">
                      <Form.Item
                        name="subcategory_id"
                        label={(
                          <Space size={6}>
                            <span>Sub-category</span>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setShowNewSubcategoryInput(true);
                                form.setFieldsValue({ subcategory_id: undefined });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setShowNewSubcategoryInput(true);
                                  form.setFieldsValue({ subcategory_id: undefined });
                                }
                              }}
                              style={{ color: '#1677ff', fontWeight: 500, cursor: 'pointer', textDecoration: 'none' }}
                            >
                              + Add sub-category
                            </span>
                          </Space>
                        )}
                        rules={[]}
                      >
                        <Select allowClear placeholder="No sub-category">
                          {subcategories.map((sub) => (
                            <Option key={sub.id} value={sub.id}>
                              {sub.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      {!chosenSubcategory && showNewSubcategoryInput && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Form.Item
                            name="subcategory_name"
                            style={{ flex: 1, marginBottom: 0, minWidth: 120 }}
                            tooltip={canCreateCategory ? 'Creates a new sub-category under selected category.' : 'You do not have category_create permission.'}
                          >
                            <Input placeholder="Enter new sub-category name" />
                          </Form.Item>
                          <Button type="primary" onClick={handleAddSubcategoryInline} loading={addingSubcategory}>
                            Add
                          </Button>
                          <Button
                            onClick={() => {
                              setShowNewSubcategoryInput(false);
                              form.setFieldsValue({ subcategory_name: undefined });
                              setSubcategoryPermissionHint('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                      {subcategoryPermissionHint && showNewSubcategoryInput && (
                        <div style={{ color: '#d4380d', fontSize: 12, marginTop: -6, marginBottom: 6 }}>
                          {subcategoryPermissionHint}
                        </div>
                      )}
                      {!canCreateCategory && showNewSubcategoryInput && (
                        <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: -6, marginBottom: 6 }}>
                          Adding sub-category requires `category_create` permission.
                        </div>
                      )}
                    </div>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <div className="product-form-section-title product-form-section-title--spaced">Pricing</div>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="unit_price"
                label="Retail selling price (per pcs)"
                rules={[{ required: true, message: 'Please enter selling price' }]}
              >
                <InputNumber
                  placeholder="0.00"
                  style={{ width: '100%' }}
                  formatter={(value) => `Nu. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/Nu.\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="cost_price"
                label="Cost Price"
                rules={[{ required: true, message: 'Please enter cost price' }]}
              >
                <InputNumber
                  placeholder="0.00"
                  style={{ width: '100%' }}
                  formatter={(value) => `Nu. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/Nu.\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="wholesale_price"
                label="Wholesale price (per pcs)"
                tooltip="Leave empty to use retail price in wholesale mode"
              >
                <InputNumber
                  placeholder="Same as retail if empty"
                  style={{ width: '100%' }}
                  formatter={(value) => `Nu. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/Nu.\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
          <ProductPackagingFields key={editingProduct?.id ?? 'new-product'} />
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="gst_category"
                label="GST for this product"
                initialValue="taxable"
                rules={[{ required: true, message: 'Choose whether this product is GST-taxable' }]}
                extra="Set this when you add the product. POS uses it for TAX INVOICE filing: taxable = customer pays company GST rate; exempt and zero-rated = customer pays 0 GST."
              >
                <Select
                  options={[
                    { value: 'taxable', label: 'Taxable (company GST rate)' },
                    { value: 'exempt', label: 'Exempt (no GST)' },
                    { value: 'zero_rated', label: 'Zero-rated (0% GST, e.g. export)' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="product-form-inventory-panel">
            <div className="product-form-section-title">Physical stock &amp; units</div>
            {editingProduct && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="On-hand quantity (actual units in inventory)"
                description={
                  editStockLoading ? (
                    <Spin size="small" />
                  ) : editStockError ? (
                    <Text type="secondary">
                      Could not load inventory for this product. You may need inventory read permission, or try again.
                    </Text>
                  ) : (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        Linked by product ID <span className="app-mono">{editingProduct.id}</span>
                        {editingProduct.code ? (
                          <>
                            {' '}
                            · Material ID <span className="app-mono">{editingProduct.code}</span>
                          </>
                        ) : null}
                      </Text>
                      <div style={{ marginTop: 10 }}>
                        <Text strong style={{ fontSize: 15 }}>
                          Total on hand:{' '}
                        </Text>
                        <Text strong style={{ fontSize: 15 }} className="app-mono">
                          {editStockSnapshot?.totalOnHand ?? 0}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                          Sum of quantity on hand across locations you can see (all branches for company admins).
                          This total is written to minimum stock level when you click Update (after it finishes loading).
                          Change counts in the Inventory page.
                        </Text>
                      </div>
                      {editStockSnapshot && editStockSnapshot.lines.length > 0 && (
                        <ul style={{ margin: '10px 0 0', paddingLeft: 20, fontSize: 12 }}>
                          {editStockSnapshot.lines.map((l) => (
                            <li key={l.locationId}>
                              {l.name}: <span className="app-mono">{l.onHand}</span> on hand
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )
                }
              />
            )}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="unit_of_measure"
                  label="Unit of Measure"
                  rules={[{ required: true, message: 'Please select unit of measure' }]}
                >
                  <Select placeholder="Select unit">
                    {UOM_OPTIONS.map((o) => (
                      <Option key={o.value} value={o.value}>
                        {o.label}
                      </Option>
                    ))}
                    <Option value={UOM_OTHER_VALUE}>Other (custom)</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="minimum_stock_level"
                  label={
                    editingProduct
                      ? 'Minimum stock level (hands-on total from inventory)'
                      : 'Low-stock alert level'
                  }
                  tooltip={
                    editingProduct
                      ? 'Filled from total on hand above after inventory loads. It is saved on Update so the catalog holds your current hands-on quantity.'
                      : 'Optional: warn when available quantity is at or below this number.'
                  }
                  rules={[
                    {
                      required: true,
                      message: editingProduct
                        ? 'Wait for inventory to finish loading, or fix the error above'
                        : 'Please enter a number (use 0 if you do not want alerts)',
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="0"
                    style={{ width: '100%' }}
                    min={0}
                    readOnly={
                      !!(
                        editingProduct &&
                        editStockSnapshot &&
                        !editStockError &&
                        !editStockLoading
                      )
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item shouldUpdate={(prev, cur) => prev.unit_of_measure !== cur.unit_of_measure} noStyle>
              {({ getFieldValue }) => {
                const isOther = getFieldValue('unit_of_measure') === UOM_OTHER_VALUE;
                if (!isOther) return null;
                return (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="unit_of_measure_other"
                        label="Custom Unit"
                        rules={[
                          { required: true, message: 'Please enter your unit (max 20 characters)' },
                          {
                            validator: async (_, value) => {
                              const v = String(value || '').trim();
                              if (!v) return Promise.reject(new Error('Please enter your unit'));
                              if (v.length > 20) return Promise.reject(new Error('Unit is too long (max 20 characters)'));
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input placeholder="e.g., bottle, pack, tablet" maxLength={20} />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>

            {!editingProduct && (
              <>
                <div className="product-form-section-title product-form-section-title--spaced">Opening on hand</div>
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
                  Add how many units you received <strong>per location</strong>. Saving updates on-hand stock for those
                  locations (you need permission to create stock adjustments). If you leave all quantities at 0, pick a
                  default location below or leave it blank to use your account default.
                </Text>
                <Form.List name="initial_location_stocks">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <Row gutter={16} key={key} align="top" wrap>
                          <Col xs={24} md={11}>
                            <Form.Item
                              {...restField}
                              name={[name, 'location_id']}
                              label={index === 0 ? 'Location' : undefined}
                            >
                              <Select
                                allowClear
                                placeholder="Select location"
                                showSearch
                                optionFilterProp="children"
                              >
                                {locations.map((loc) => (
                                  <Option key={loc.id} value={loc.id}>
                                    {loc.name}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={9}>
                            <Form.Item
                              {...restField}
                              name={[name, 'quantity']}
                              label={index === 0 ? 'Quantity received' : undefined}
                              initialValue={0}
                            >
                              <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={4} style={{ display: 'flex', alignItems: index === 0 ? 'flex-end' : 'center', paddingBottom: index === 0 ? 24 : 0 }}>
                            {fields.length > 1 ? (
                              <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}>
                                Remove
                              </Button>
                            ) : null}
                          </Col>
                        </Row>
                      ))}
                      <Button
                        type="dashed"
                        onClick={() => add({ location_id: undefined, quantity: 0 })}
                        block
                        icon={<PlusOutlined />}
                        style={{ marginBottom: 16 }}
                      >
                        Add another location
                      </Button>
                    </>
                  )}
                </Form.List>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="inventory_location_id"
                      label="Default stock location"
                      tooltip="When every opening quantity is 0: empty stock row is created here (or your user default if blank)."
                    >
                      <Select
                        allowClear
                        placeholder="Your default branch"
                        showSearch
                        optionFilterProp="children"
                      >
                        {locations.map((loc) => (
                          <Option key={loc.id} value={loc.id}>
                            {loc.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </div>

          <div className="product-form-section-title product-form-section-title--spaced">Description</div>
          <Form.Item
            name="description"
            label={null}
            colon={false}
          >
            <Input.TextArea placeholder="Product description…" rows={4} autoComplete="off" />
          </Form.Item>

          {editingProduct && (
            <Form.Item
              name="is_active"
              label="Active Status"
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}
        </Form>
        </div>
      </Modal>

      {/* View product — same shell as Purchase Order details (light header, full-width panel, summary + body) */}
      <ModernFormModal
        open={viewProductModalVisible}
        onClose={() => {
          setViewProductModalVisible(false);
          setViewingProduct(null);
        }}
        title={`Product Details — ${viewingProduct?.name ?? ''}`}
        subtitle="View catalog information for this product."
        headerVariant="light"
        headerSummary={
          viewingProduct ? (
            <>
              <div>
                <span className="mf-summary-label">Material ID:</span>{' '}
                <span className="mf-summary-value app-mono">{viewingProduct.code}</span>
              </div>
              <div>
                <span className="mf-summary-label">Category:</span>{' '}
                <span className="mf-summary-value">
                  {formatCategoryBreadcrumb(categories, viewingProduct.category_id) ||
                    viewingProduct.category?.name ||
                    '—'}
                </span>
              </div>
              <div>
                <span className="mf-summary-label">Status:</span>{' '}
                <Tag color={viewingProduct.is_active ? 'success' : 'default'}>
                  {viewingProduct.is_active ? 'Active' : 'Inactive'}
                </Tag>
              </div>
              <div>
                <span className="mf-summary-label">Selling price:</span>{' '}
                <span className="mf-summary-value">
                  Nu. {Number(viewingProduct.selling_price ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div>
                <span className="mf-summary-label">Unit:</span>{' '}
                <span className="mf-summary-value">{viewingProduct.unit_of_measure || '—'}</span>
              </div>
            </>
          ) : null
        }
        getContainer={() => document.querySelector('.main-content-full') as HTMLElement || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Close"
        showOkButton={canUpdateProduct}
        okText="Edit product"
        okIcon={<EditOutlined />}
        onOk={() => {
          const p = viewingProduct;
          setViewProductModalVisible(false);
          setViewingProduct(null);
          if (p) handleEdit(p);
        }}
      >
        {viewingProduct && (
          <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <p style={{ fontSize: '12px', fontWeight: 400 }}>
                  <span style={{ fontWeight: 400 }}>Product name:</span> {viewingProduct.name}
                </p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}>
                  <span style={{ fontWeight: 400 }}>Material ID:</span>{' '}
                  <span className="app-mono">{viewingProduct.code}</span>
                </p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}>
                  <span style={{ fontWeight: 400 }}>Barcode:</span>{' '}
                  {viewingProduct.barcode != null && String(viewingProduct.barcode).trim() !== ''
                    ? String(viewingProduct.barcode)
                    : '—'}
                </p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}>
                  <span style={{ fontWeight: 400 }}>Category:</span>{' '}
                  {formatCategoryBreadcrumb(categories, viewingProduct.category_id) ||
                    viewingProduct.category?.name ||
                    '—'}
                </p>
              </Col>
              <Col xs={24} md={12}>
                <p style={{ fontSize: '12px', fontWeight: 400 }}>
                  <span style={{ fontWeight: 400 }}>Status:</span>{' '}
                  <Tag color={viewingProduct.is_active ? 'success' : 'default'}>
                    {viewingProduct.is_active ? 'Active' : 'Inactive'}
                  </Tag>
                </p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}>
                  <span style={{ fontWeight: 400 }}>Unit of measure:</span> {viewingProduct.unit_of_measure || '—'}
                </p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}>
                  <span style={{ fontWeight: 400 }}>Created:</span>{' '}
                  {viewingProduct.created_at
                    ? moment(viewingProduct.created_at).format('DD MMM YYYY, HH:mm')
                    : '—'}
                </p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}>
                  <span style={{ fontWeight: 400 }}>Last updated:</span>{' '}
                  {viewingProduct.updated_at
                    ? moment(viewingProduct.updated_at).format('DD MMM YYYY, HH:mm')
                    : '—'}
                </p>
              </Col>
            </Row>

            <Divider />

            <Title level={5} style={{ fontSize: '12px', fontWeight: 400 }}>
              Pricing &amp; stock settings
            </Title>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              dataSource={[
                {
                  key: 'sell',
                  field: 'Selling price',
                  value: `Nu. ${Number(viewingProduct.selling_price ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                },
                {
                  key: 'cost',
                  field: 'Cost price',
                  value: `Nu. ${Number(viewingProduct.cost_price ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                },
                {
                  key: 'gst',
                  field: 'GST',
                  value: gstCategoryLabel(viewingProduct.gst_category),
                },
                {
                  key: 'reorder',
                  field: 'Low-stock alert level',
                  value:
                    viewingProduct.minimum_stock_level != null
                      ? String(viewingProduct.minimum_stock_level)
                      : viewingProduct.reorder_level != null
                        ? String(viewingProduct.reorder_level)
                        : '—',
                },
                {
                  key: 'onhand',
                  field: 'Stock on hand (reported)',
                  value:
                    viewingProduct.stock_level != null && viewingProduct.stock_level !== undefined
                      ? String(viewingProduct.stock_level)
                      : '—',
                },
              ]}
              columns={[
                { title: 'Field', dataIndex: 'field', key: 'field', width: 200 },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  key: 'value',
                  render: (v: string) => (
                    <Text style={{ fontSize: '12px', fontWeight: 400 }}>{v}</Text>
                  ),
                },
              ]}
            />

            {viewingProduct.description != null && String(viewingProduct.description).trim() !== '' && (
              <>
                <Divider />
                <p style={{ fontSize: '12px', fontWeight: 400 }}>
                  <span style={{ fontWeight: 400 }}>Description:</span>
                </p>
                <p style={{ fontSize: '12px', fontWeight: 400, whiteSpace: 'pre-wrap' }}>{viewingProduct.description}</p>
              </>
            )}
          </div>
        )}
      </ModernFormModal>
    </div>
  );
};

export default Products;